import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import type { DocumentNode } from '@apollo/client';
import { logAppError } from '../utils/errorUtils';

type RefetchQuery = {
  query: DocumentNode;
  variables: Record<string, any>;
  name: string;
};

type UseRefetchQueriesWithRetryOptions = {
  errorContext: string;
  maxAttempts?: number;
};

/**
 * TEMPORARY: This hook is part of the gradual migration from GraphQL/Apollo to React Query.
 * It provides a bridge solution for refetching Apollo queries after React Query mutations.
 *
 * NOTE: This is duplicated in satellite-rachel-web. Once we fully migrate to React Query's
 * proxy API, we'll use proper cache invalidation via `invalidates` meta tags instead.
 * At that point, this hook will be deleted from both apps.
 *
 * TODO: Replace with React Query's invalidateQueries once proxy API migration is complete.
 *
 * Custom hook that provides a function to refetch Apollo queries with retry logic.
 * Useful for refetching queries after a successful mutation when you want resilience against temporary network issues.
 *
 * @param options.errorContext - Context string for error logging (e.g., "component.AddMessage.refetchQueries")
 * @param options.maxAttempts - Maximum number of retry attempts (default: 3)
 *
 * @returns A function that accepts an array of queries to refetch with retry logic
 *
 * @example
 * const refetchWithRetry = useRefetchQueriesWithRetry({
 *   errorContext: 'component.AddMessage.refetchQueries',
 * });
 *
 * // After successful mutation
 * refetchWithRetry([
 *   {
 *     query: MessageListDocument,
 *     variables: { conversationId },
 *     name: 'MessageList',
 *   },
 *   {
 *     query: ConversationListDocument,
 *     variables: { folder: 'inbox' },
 *     name: 'ConversationList',
 *   },
 * ]);
 */
export const useRefetchQueriesWithRetry = ({ errorContext, maxAttempts = 3 }: UseRefetchQueriesWithRetryOptions) => {
  const apolloClient = useApolloClient();

  const refetchWithRetry = useCallback(
    async (queryFn: () => Promise<any>, queryName: string) => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          await queryFn();
          return; // Success
        } catch (refetchError) {
          if (attempt === maxAttempts) {
            // All retries failed - log but don't throw
            logAppError({
              context: { errorLocation: errorContext },
              message: `Failed to refetch ${queryName} after ${maxAttempts} attempts`,
              error: refetchError,
            });
          } else {
            // Wait before next attempt (linear backoff based on attempt number)
            // With 3 total attempts: attempt 1 → wait 1s → attempt 2 → wait 2s → attempt 3
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          }
        }
      }
    },
    [apolloClient, errorContext, maxAttempts],
  );

  const refetchQueries = useCallback(
    (queries: RefetchQuery[]) => {
      // Fire all refetches in parallel, don't await completion to avoid blocking UI
      Promise.all(
        queries.map((query) =>
          refetchWithRetry(
            () =>
              apolloClient.query({
                query: query.query,
                variables: query.variables,
                fetchPolicy: 'network-only',
              }),
            query.name,
          ),
        ),
      ).catch(() => {
        // Silently catch - errors are already logged in refetchWithRetry
      });
    },
    [apolloClient, refetchWithRetry],
  );

  return refetchQueries;
};
